import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth, OAuthProvider, type Auth } from "firebase/auth";

type FirebaseOidcClientOptions = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  appId?: string;
  providerId?: string;
};

type InitializedFirebaseOidcClient = {
  auth: Auth | null;
  provider: OAuthProvider | null;
  error: string | null;
};

export let globalFirebaseOidcOptions: FirebaseOidcClientOptions | null = null;

let firebaseOidcClient: InitializedFirebaseOidcClient | null = null;
let firebaseOidcError: string | null = null;

export const configureFirebaseOidcClient = (
  options: FirebaseOidcClientOptions
) => {
  globalFirebaseOidcOptions = options;
  firebaseOidcClient = null;

  const { apiKey, authDomain, projectId, appId, providerId } = options;
  firebaseOidcError =
    !apiKey || !authDomain || !projectId || !appId || !providerId
      ? "loginSSOPage.firebaseConfigError"
      : null;
};

export const ensureFirebaseOidcClientConfigured = () => {
  if (globalFirebaseOidcOptions?.providerId) {
    return;
  }

  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  if (!apiKey) {
    return;
  }

  configureFirebaseOidcClient({
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    providerId: import.meta.env.VITE_FIREBASE_OIDC_PROVIDER_ID,
  });
};

export const getFirebaseOidcClient = (): InitializedFirebaseOidcClient => {
  if (firebaseOidcClient) {
    return firebaseOidcClient;
  }

  if (firebaseOidcError || !globalFirebaseOidcOptions) {
    return {
      auth: null,
      provider: null,
      error: firebaseOidcError ?? "loginSSOPage.firebaseConfigError",
    };
  }

  const { apiKey, authDomain, projectId, appId, providerId } =
    globalFirebaseOidcOptions;

  try {
    const firebaseApp =
      getApps().length > 0
        ? getApp()
        : initializeApp({
            apiKey: apiKey!,
            authDomain: authDomain!,
            projectId: projectId!,
            appId: appId!,
          });

    const auth = getAuth(firebaseApp);
    const provider = new OAuthProvider(providerId!);
    provider.addScope("email");
    provider.addScope("profile");

    firebaseOidcClient = {
      auth,
      provider,
      error: null,
    };

    return firebaseOidcClient;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to initialize Firebase", error);
    firebaseOidcError = "loginSSOPage.firebaseConfigError";

    return {
      auth: null,
      provider: null,
      error: firebaseOidcError,
    };
  }
};
